import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InfoCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const EnvironmentInfoIndicator = () => {
    const [envInfo, setEnvInfo] = useState({
        isVisible: false,
        company: '',
        env: '',
        baseUrl: '',
        apiUrl: '',
        onContinue: null,
        onCancel: null
    });
    const [progress, setProgress] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState(15);
    const intervalRef = useRef(null);

    const handleContinue = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        const currentOnContinue = envInfo.onContinue;
        setEnvInfo(prev => ({ ...prev, isVisible: false }));
        if (currentOnContinue) {
            currentOnContinue();
        }
    }, [envInfo.onContinue]);

    const handleCancel = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        const currentOnCancel = envInfo.onCancel;
        setEnvInfo(prev => ({ ...prev, isVisible: false }));
        if (currentOnCancel) {
            currentOnCancel();
        }
    }, [envInfo.onCancel]);

    useEffect(() => {
        const handleEnvInfo = (event) => {
            const { company, env, baseUrl, apiUrl, onContinue, onCancel } = event.detail;

            setEnvInfo({
                isVisible: true,
                company,
                env,
                baseUrl,
                apiUrl,
                onContinue,
                onCancel
            });

            setProgress(0);
            setTimeRemaining(15);

            // Limpiar cualquier intervalo anterior
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }

            // Iniciar el temporizador de 15 segundos
            let secondsElapsed = 0;
            intervalRef.current = setInterval(() => {
                secondsElapsed += 0.1;
                const newProgress = (secondsElapsed / 15) * 100;
                const newTimeRemaining = Math.max(0, 15 - Math.floor(secondsElapsed));

                setProgress(newProgress);
                setTimeRemaining(newTimeRemaining);

                if (secondsElapsed >= 15) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    // Llamar directamente la función onContinue
                    if (onContinue) {
                        onContinue();
                    }
                    setEnvInfo(prev => ({ ...prev, isVisible: false }));
                }
            }, 100);
        };

        window.addEventListener('environmentInfo', handleEnvInfo);

        return () => {
            window.removeEventListener('environmentInfo', handleEnvInfo);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    if (!envInfo.isVisible) {
        return null;
    }

    return (
        <div
            style={{
                position: 'fixed',
                bottom: '20px',
                left: '20px',
                backgroundColor: '#1677ff',
                color: 'white',
                padding: '16px',
                borderRadius: '8px',
                fontSize: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                zIndex: 999,
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                maxWidth: '380px',
                minWidth: '320px',
                animation: 'slideIn 0.3s ease-out'
            }}
        >
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px',
                fontSize: '13px',
                fontWeight: 'bold'
            }}>
                <InfoCircleOutlined style={{ fontSize: '14px' }} />
                <span>Información del Entorno</span>
            </div>

            <div style={{ fontSize: '11px', lineHeight: '1.4', marginBottom: '8px' }}>
                <div style={{ marginBottom: '2px' }}>
                    <strong>Compañía:</strong> {envInfo.company}
                </div>
                <div style={{ marginBottom: '2px' }}>
                    <strong>Env:</strong> {envInfo.env}
                </div>
                <div style={{ marginBottom: '2px' }}>
                    <strong>Base URL:</strong> {envInfo.baseUrl}
                </div>
                <div>
                    <strong>API URL:</strong> {envInfo.apiUrl}
                </div>
            </div>

            {/* Barra de progreso */}
            <div style={{ marginBottom: '8px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '10px',
                    marginBottom: '4px'
                }}>
                    <span>Sincronización en {timeRemaining}s</span>
                    <span>{Math.round(progress)}%</span>
                </div>
                <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: 'white',
                        transition: 'width 0.1s ease-out'
                    }} />
                </div>
            </div>

            {/* Botones */}
            <div style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end'
            }}>
                <button
                    onClick={handleCancel}
                    style={{
                        backgroundColor: '#ff4d4f',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    <CloseOutlined style={{ fontSize: '10px' }} />
                    Cancelar
                </button>
                <button
                    onClick={handleContinue}
                    style={{
                        backgroundColor: '#52c41a',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    <CheckOutlined style={{ fontSize: '10px' }} />
                    Continuar
                </button>
            </div>
        </div>
    );
};

export default EnvironmentInfoIndicator; 